// New bracket dialog: single / double elimination, Swiss (fixed rounds or Major style) and
// round-robin groups - every format of the rebuilt bracket engine.
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';
import DatePicker from '../DatePicker';
import TeamLogo from '../teams/TeamLogo';
import { PlayoffFields, playoffTeams } from './PlayoffsModal';
import './bracket.css';

const nextPow2 = (n) => { let p = 1; while (p < n) p *= 2; return p; };

export default function CreateBracketModal({ isOpen, onClose, seasonId, teams, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState('Main Bracket');
  const [date, setDate] = useState('');
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState('');
  const [third, setThird] = useState(false);
  const [type, setType] = useState('single_elimination');
  const [gfMode, setGfMode] = useState('simple');
  const [bo, setBo] = useState({ early: 1, semi: 3, final: 3, third: 1, gf: 3, stage: 1 });
  const double = type === 'double_elimination';
  const isMajor = type === 'major'; // = a Swiss stage preset to the Major rules (+ playoffs)
  const isSwiss = type === 'swiss' || isMajor;
  const isGroups = type === 'group_stage';
  const stageType = isSwiss || isGroups;
  const [swissMode, setSwissMode] = useState('rounds');
  const [swissRounds, setSwissRounds] = useState(0); // 0 = ceil(log2 teams)
  const [swissWL, setSwissWL] = useState({ w: 3, l: 3 });
  const [deciderBo, setDeciderBo] = useState(0); // Major: best-of of the matches that qualify / eliminate (0 = round's)
  const [numGroups, setNumGroups] = useState(2);
  const [groupFormat, setGroupFormat] = useState('round_robin'); // round_robin | dual (GSL: 4 teams, double elim)
  const [groupFinal, setGroupFinal] = useState(false); // GSL: winners' match winner v decider winner → 1st place
  const [playoffs, setPlayoffs] = useState(null); // Swiss / groups → playoffs (two-stage bracket)
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const n = picked.length;
  const size = n >= 2 ? nextPow2(n) : 0;
  const rounds = size ? Math.log2(size) : 0;
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return [...(teams || [])].filter(tm => !s || String(tm.name).toLowerCase().includes(s) || String(tm.tag || '').toLowerCase().includes(s))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [teams, q]);

  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  // Picking a format: "Major" fills in the CS Major rules - Swiss to 3 wins / 3 losses, deciding
  // matches BO3, others BO1, top 8 into single-elimination playoffs (BO3, final BO5). All still editable.
  const pickType = (key) => {
    setType(key);
    if (key === 'major') {
      setSwissMode('major'); setSwissWL({ w: 3, l: 3 }); setDeciderBo(3); setBo(b => ({ ...b, stage: 1 }));
      setPlayoffs({ bracket_type: 'single_elimination', advance: 8, has_third_place: false, grand_final_mode: 'simple',
        stage_formats: [{ from_round: 1, to_round: 2, best_of: 3 }, { from_round: 3, to_round: 3, best_of: 5 }] });
    } else if (type === 'major') {
      setDeciderBo(0); setPlayoffs(null);
    }
  };
  // group sizes the server will make (snake distribution: sizes differ by at most 1)
  const groupCount = Math.max(1, Math.min(numGroups, Math.floor(n / 2) || 1));
  const groupSizes = Array.from({ length: groupCount }, (_, i) => Math.floor(n / groupCount) + (i < n % groupCount ? 1 : 0));
  const playoffTotal = playoffTeams(playoffs, { isGroups, groupSizes, teamCount: n });

  const formats = () => {
    const fm = [];
    if (stageType) return [{ from_round: 1, to_round: 99, best_of: bo.stage }];
    if (double) {
      // upper r, lower 200 + r, grand final 300 (same virtual rounds as the server)
      const L = 2 * (rounds - 1);
      for (let r = 1; r <= rounds; r++) fm.push({ from_round: r, to_round: r, best_of: r === rounds ? bo.final : bo.early });
      for (let r = 1; r <= L; r++) fm.push({ from_round: 200 + r, to_round: 200 + r, best_of: r === L ? bo.final : bo.early });
      fm.push({ from_round: 300, to_round: 300, best_of: bo.gf });
      return fm;
    }
    for (let r = 1; r <= rounds; r++) {
      const left = rounds - r;
      fm.push({ from_round: r, to_round: r, best_of: left === 0 ? bo.final : left === 1 ? bo.semi : bo.early });
    }
    fm.push({ from_round: rounds + 100, to_round: rounds + 100, best_of: bo.third });
    return fm;
  };

  const submit = async () => {
    setErr('');
    if (!name.trim()) { setErr(t('bracket.create.nameRequired', 'Give the bracket a name.')); return; }
    if (n < 2) { setErr(t('bracket.create.pickTwo', 'Pick at least 2 teams.')); return; }
    if (stageType && playoffs && playoffTotal < 2) { setErr(t('bracket.po.tooFew', 'At least 2 teams must go through.')); return; }
    if (isGroups && groupFormat === 'dual' && (n < 3 * groupCount || n > 4 * groupCount)) {
      setErr(t('bracket.gsl.sizeErr', 'GSL groups need 3-4 teams each: {{g}} group(s) take {{a}}-{{b}} teams.', { g: groupCount, a: 3 * groupCount, b: 4 * groupCount }));
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post('/api/brackets', {
        season_id: Number(seasonId), name: name.trim(), bracket_type: isSwiss ? 'swiss' : type,
        team_ids: picked, team_count: n, has_third_place: type === 'single_elimination' && third && n >= 3, grand_final_mode: double ? gfMode : undefined,
        stage_formats: formats(), scheduled_date: date || null,
        ...(isSwiss ? { swiss_mode: swissMode, swiss_rounds: swissRounds || undefined, swiss_wins_to_qualify: swissWL.w, swiss_losses_to_eliminate: swissWL.l,
          swiss_decider_bo: swissMode === 'major' && deciderBo ? deciderBo : undefined } : {}),
        ...(isGroups ? { num_groups: numGroups, group_best_of: bo.stage, group_format: groupFormat, gsl_group_final: groupFormat === 'dual' && groupFinal } : {}),
        ...(stageType && playoffs ? { playoffs: { ...playoffs, has_third_place: playoffs.has_third_place && playoffTotal >= 4 } } : {}),
      });
      onCreated(res.data.bracket.id);
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setBusy(false); }
  };

  const boSel = (key) => (
    <select className="br-select full" value={bo[key]} onChange={e => setBo({ ...bo, [key]: Number(e.target.value) })}>
      {[1, 3, 5].map(b => <option key={b} value={b}>BO{b}</option>)}
    </select>
  );

  const TYPES = [
    { key: 'single_elimination', h: t('bracket.type.single', 'Single elimination'), d: t('bracket.type.singleDesc', 'Out after one loss') },
    { key: 'double_elimination', h: t('bracket.type.double', 'Double elimination'), d: t('bracket.type.doubleDesc', 'Out after two losses') },
    { key: 'swiss', h: t('bracket.type.swiss', 'Swiss'), d: t('bracket.type.swissDesc', 'Same records play each round') },
    { key: 'group_stage', h: t('bracket.type.groups', 'Group stage'), d: t('bracket.type.groupsDesc', 'Groups, then playoffs') },
    { key: 'major', h: t('bracket.type.major', 'Major'), d: t('bracket.type.majorDesc', 'Swiss to 3 wins / 3 losses, top 8 to playoffs') },
  ];

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} title={t('bracket.create.title', 'New bracket')} maxWidth="680px"
      footer={<>
        <button className="br-btn ghost" onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
        <button className="br-btn" onClick={submit} disabled={busy || n < 2}>{t('bracket.create.submit', 'Create bracket')}</button>
      </>}>
      <div className="br-form">
        <div className="br-field">
          <span className="lbl">{t('bracket.create.format', 'Format')}</span>
          <div className="br-types">
            {TYPES.map(x => (
              <button key={x.key} type="button" className={`br-type${x.key === type ? ' on' : ''}`}
                onClick={() => pickType(x.key)}>
                <div className="h">{x.h}</div><div className="d">{x.d}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="br-grid2">
          <div className="br-field">
            <label htmlFor="br-name">{t('bracket.create.name', 'Name')}</label>
            <input id="br-name" className="br-input" value={name} onChange={e => setName(e.target.value)} maxLength={80} />
          </div>
          <div className="br-field">
            <DatePicker label={t('bracket.create.date', 'Date (optional)')} value={date} onChange={setDate} />
          </div>
        </div>

        <div className="br-field">
          <span className="lbl">{t('bracket.create.teams', 'Teams - click in seed order (seed 1 first)')}</span>
          <input className="br-input" style={{ marginBottom: '0.4rem' }} placeholder={t('bracket.create.search', 'Search teams…')} value={q} onChange={e => setQ(e.target.value)} />
          <div className="br-pick">
            {list.map(tm => {
              const i = picked.indexOf(tm.id);
              return (
                <div key={tm.id} className={`br-pick-row${i >= 0 ? ' on' : ''}`} onClick={() => toggle(tm.id)}>
                  <span className="chk">{i >= 0 ? `#${i + 1}` : ''}</span>
                  <TeamLogo team={tm} size={20} radius={5} />
                  <span>{tm.name}</span>
                </div>
              );
            })}
            {list.length === 0 && <div className="br-hint" style={{ padding: '0.6rem' }}>{t('bracket.create.noTeams', 'No teams found.')}</div>}
          </div>
          <div className="br-summary" style={{ marginTop: '0.45rem' }}>
            <span className="br-pill">{t('bracket.teamsN', '{{n}} teams', { n })}</span>
            {!stageType && size > 0 && <span className="br-pill">{t('bracket.create.size', 'Bracket of {{n}}', { n: size })}</span>}
            {!stageType && size > n && <span className="br-pill" style={{ color: 'var(--accent-amber)' }}>{t('bracket.create.byes', '{{n}} byes for the top seeds', { n: size - n })}</span>}
            {n > 0 && <button type="button" className="br-btn ghost sm" onClick={() => setPicked([])}>{t('bracket.create.clear', 'Clear')}</button>}
          </div>
        </div>

        <div className="br-field">
          <span className="lbl">{t('bracket.create.formats', 'Best of')}</span>
          <div className="br-grid2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            {stageType ? <div><div className="br-hint">{isSwiss ? t('bracket.create.everyRound', 'Every round') : t('bracket.create.everyMatch', 'Every group match')}</div>{boSel('stage')}</div>
            : double ? <>
              <div><div className="br-hint">{t('bracket.create.early', 'Early rounds')}</div>{boSel('early')}</div>
              <div><div className="br-hint">{t('bracket.create.ulFinals', 'Upper & lower final')}</div>{boSel('final')}</div>
              {gfMode !== 'none' && <div><div className="br-hint">{t('bracket.round.grandFinal', 'Grand final')}</div>{boSel('gf')}</div>}
            </> : <>
              {rounds > 2 && <div><div className="br-hint">{t('bracket.create.early', 'Early rounds')}</div>{boSel('early')}</div>}
              {rounds > 1 && <div><div className="br-hint">{t('bracket.round.semifinal', 'Semifinal')}</div>{boSel('semi')}</div>}
              <div><div className="br-hint">{t('bracket.round.final', 'Final')}</div>{boSel('final')}</div>
              {third && n >= 3 && <div><div className="br-hint">{t('bracket.round.thirdPlace', '3rd place')}</div>{boSel('third')}</div>}
            </>}
          </div>
        </div>

        {double && (
          <div className="br-field">
            <span className="lbl">{t('bracket.gf.label', 'Grand final')}</span>
            <div className="br-types" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[
                ['simple', t('bracket.gf.simple', 'One match'), t('bracket.gf.simpleDesc', 'Upper winner vs lower winner, one series')],
                ['reset', t('bracket.gf.reset', 'With reset'), t('bracket.gf.resetDesc', 'If the lower winner wins, a second series decides')],
                ['none', t('bracket.gf.none', 'None - upper final decides'), t('bracket.gf.noneDesc', 'No grand final, the lower final decides 2nd place')],
              ].map(([k, h, d]) => (
                <button key={k} type="button" className={`br-type${gfMode === k ? ' on' : ''}`} onClick={() => setGfMode(k)}>
                  <div className="h">{h}</div><div className="d">{d}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {isSwiss && (
          <div className="br-field">
            <span className="lbl">{t('bracket.swiss.end', 'Ends')}</span>
            <div className="br-types" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {[
                ['rounds', t('bracket.swiss.modeRounds', 'After fixed rounds'), t('bracket.swiss.modeRoundsDesc', 'Everyone plays every round; ranking at the end')],
                ['major', t('bracket.swiss.modeMajor', 'At X wins / X losses (Major)'), t('bracket.swiss.modeMajorDesc', 'Teams play until they qualify or are out')],
              ].map(([k, h, d]) => (
                <button key={k} type="button" className={`br-type${swissMode === k ? ' on' : ''}`} onClick={() => setSwissMode(k)}><div className="h">{h}</div><div className="d">{d}</div></button>
              ))}
            </div>
            <div className="br-row" style={{ marginTop: '0.5rem' }}>
              {swissMode === 'rounds' ? (
                <label className="br-format">{t('bracket.swiss.rounds', 'Rounds')}
                  <select value={swissRounds} onChange={e => setSwissRounds(Number(e.target.value))}>
                    <option value={0}>{t('bracket.swiss.auto', 'Auto ({{n}})', { n: Math.max(1, Math.ceil(Math.log2(Math.max(2, n)))) })}</option>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(x => <option key={x} value={x}>{x}</option>)}
                  </select></label>
              ) : (
                <>
                  <label className="br-format">{t('bracket.swiss.wins', 'Wins to qualify')}
                    <select value={swissWL.w} onChange={e => setSwissWL({ ...swissWL, w: Number(e.target.value) })}>{[1, 2, 3, 4, 5].map(x => <option key={x} value={x}>{x}</option>)}</select></label>
                  <label className="br-format">{t('bracket.swiss.losses', 'Losses to be out')}
                    <select value={swissWL.l} onChange={e => setSwissWL({ ...swissWL, l: Number(e.target.value) })}>{[1, 2, 3, 4, 5].map(x => <option key={x} value={x}>{x}</option>)}</select></label>
                  <label className="br-format" title={t('bracket.swiss.deciderHint', 'Matches where a team can qualify or be eliminated')}>{t('bracket.swiss.decider', 'Deciding matches')}
                    <select value={deciderBo} onChange={e => setDeciderBo(Number(e.target.value))}>
                      <option value={0}>{t('bracket.swiss.deciderSame', 'Same as the round')}</option>
                      {[1, 3, 5].map(x => <option key={x} value={x}>BO{x}</option>)}
                    </select></label>
                </>
              )}
            </div>
          </div>
        )}

        {isGroups && (
          <div className="br-field">
            <span className="lbl">{t('bracket.groups.format', 'Group format')}</span>
            <div className="br-types" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {[
                ['round_robin', t('bracket.groups.formatRr', 'Round robin'), t('bracket.groups.formatRrDesc', 'Everyone plays everyone in the group')],
                ['dual', t('bracket.groups.formatGsl', 'GSL'), t('bracket.groups.formatGslDesc', '4 teams, double elimination: opening, winners\', elimination, decider')],
              ].map(([k, h, d]) => (
                <button key={k} type="button" className={`br-type${groupFormat === k ? ' on' : ''}`} onClick={() => setGroupFormat(k)}><div className="h">{h}</div><div className="d">{d}</div></button>
              ))}
            </div>
            {groupFormat === 'dual' && (
              <label className="br-row" style={{ cursor: 'pointer', gap: '0.6rem', marginTop: '0.5rem' }}>
                <button type="button" className={`br-toggle${groupFinal ? ' on' : ''}`} onClick={() => setGroupFinal(v => !v)}><span /></button>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('bracket.gsl.final', 'Group final')}</span>
                <span className="br-hint">{t('bracket.gsl.finalHint', "Winners' match winner v decider winner - the winner is the group's 1st place (for stages where one team per group goes through)")}</span>
              </label>
            )}
          </div>
        )}

        {isGroups && (
          <div className="br-field">
            <span className="lbl">{t('bracket.groups.count', 'Groups')}</span>
            <div className="br-row">
              <select className="br-select" value={numGroups} onChange={e => setNumGroups(Number(e.target.value))}>
                {Array.from({ length: 8 }, (_, i) => i + 1).map(x => <option key={x} value={x}>{x}</option>)}
              </select>
              {n >= 2 && <span className="br-hint">{t('bracket.groups.sizes', '{{n}} teams → groups of {{a}}-{{b}}', { n, a: Math.floor(n / Math.min(numGroups, Math.floor(n / 2) || 1)), b: Math.ceil(n / Math.min(numGroups, Math.floor(n / 2) || 1)) })}</span>}
            </div>
          </div>
        )}

        {stageType && (
          <div className="br-card" style={{ padding: '0.6rem 0.75rem' }}>
            <PlayoffFields value={playoffs} onChange={setPlayoffs} isGroups={isGroups} groupSizes={groupSizes} teamCount={n} />
          </div>
        )}

        {type === 'single_elimination' && <label className="br-row" style={{ cursor: 'pointer', gap: '0.6rem' }}>
          <button type="button" className={`br-toggle${third ? ' on' : ''}`} onClick={() => setThird(v => !v)} disabled={n > 0 && n < 3}><span /></button>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t('bracket.thirdPlaceMatch', '3rd place match')}</span>
          <span className="br-hint">{t('bracket.create.thirdHint', 'Semi-final losers play for 3rd.')}</span>
        </label>}

        <div className="br-hint">{t('bracket.create.after', 'After creating you can still change the seeding. Start the bracket when it is ready - then create each match on a server from its card.')}</div>
        {err && <div className="br-err">{err}</div>}
      </div>
    </AnimatedModal>
  );
}
